/**
 * Execution block: switches a RuntimeToggleable behavior on or off at play
 * time — "when tapped, start following/facing". Trace extension.
 *
 * Config: target (path), behavior (behavior type name, e.g. "Tether",
 * "Billboard"). Data input: enabled (bool, default true).
 *
 * Semantics live on the behavior (see runtimeToggleable.ts): the authored
 * `enabled` field is the state at every play-mode start; a runtime disable on
 * Billboard applies its onExit (ease back / hold), on Tether it stops in
 * place and re-enabling re-runs `delay`. Targets without the named behavior,
 * or whose behavior isn't toggleable, warn and no-op.
 */

import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeBoolean } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { resolveTarget } from './traceContext';
import { isRuntimeToggleable } from '../../runtimeToggleable';
import { Logger } from '../../../utils/Logger';

export class TraceSetBehaviorEnabledBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;
    private _behaviorType: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string; behavior?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this._behaviorType = config?.behavior ?? '';
        this.registerDataInput("enabled", RichTypeBoolean, true);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (target && this._behaviorType) {
            // Dynamic type name from graph data — the string overload is the
            // intended lookup here.
            const behavior = target.getBehavior(this._behaviorType);
            if (behavior && isRuntimeToggleable(behavior)) {
                const enabled = this.getDataInput("enabled")!.getValue(context) as boolean;
                behavior.setRuntimeEnabled(enabled);
            } else {
                Logger.warn(`[TraceSetBehaviorEnabledBlock] "${target.name}" has no toggleable "${this._behaviorType}" behavior`);
            }
        }
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceSetBehaviorEnabledBlock";
    }
}
